import { useState } from 'react';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { NJButton, NJFormItem, NJIcon, NJInlineMessage, NJText, NJToggle, NJTooltip } from '@engie-group/fluid-design-system-react';
import { EditableRowTable } from './EditableRows';
import { CONSTRAINT_LABELS, DEFAULT_CALORIFIC_VALUE, FUEL_CONTRACT_NAME_MAX } from './fixtures';
import { CONSTRAINT_KEYS, type ConstraintKey, type FuelContract, type VariablePrice } from './types';
import { CreationInfo, inputValue, MandatoryNotice, toNumberOrNull, RightSidePanel } from './ui';

export interface FuelContractSubmit {
    name: string;
    calorificValue: number;
    fixedPrice: number;
    description: string;
    variablePrices: VariablePrice[];
    constraints: Record<ConstraintKey, number | null>;
    startHour: number | null;
    startDay: number | null;
    startMonth: number | null;
}

type StartKey = 'startHour' | 'startDay' | 'startMonth';
/** Nemo `AdvancedFuelContractFields`: start fields first, then yearly → hourly min/max (labels without unit). */
const START_FIELDS: { key: StartKey; id: string; label: string; min: number; max: number; range: string }[] = [
    { key: 'startHour', id: 'fuel-contract-combustible-start-hour', label: 'Heure de début des contraintes annuelles (Europe/Paris)', min: 0, max: 23, range: "L'heure doit être comprise entre 0 et 23" },
    { key: 'startDay', id: 'fuel-contract-combustible-start-day', label: 'Jour de début des contraintes annuelles', min: 1, max: 31, range: 'Le jour doit être compris entre 1 et 31' },
    { key: 'startMonth', id: 'fuel-contract-combustible-start-month', label: 'Mois de début des contraintes annuelles', min: 1, max: 12, range: 'Le mois doit être compris entre 1 et 12' },
];
const ADVANCED_ORDER: ConstraintKey[] = ['minYearly', 'maxYearly', 'minMonthly', 'maxMonthly', 'minDaily', 'maxDaily', 'minHourly', 'maxHourly'];
const START_ALL_OR_NONE = 'Tous les champs de début des contraintes annuelles doivent être remplis ensemble ou laissés vides';
const WHOLE_NUMBER = 'Ce champ doit être un nombre entier';
const POSITIVE = 'Ce champ doit être un nombre supérieur ou égal à 0';

type PriceRow = { key: number; date: string; price: string };
let keySeed = 0;
const nextKey = () => ++keySeed;
const emptyPriceRow = (): PriceRow => ({ key: nextKey(), date: '', price: '' });
/** Nemo `DATE_FORMAT_REGEX`: yyyy-mm, yyyy-mm-dd or yyyy-mm-dd hh. */
const DATE_FORMAT_REGEX = /^(\d{4})-(\d{2})(-(\d{2})(\s(\d{2}))?)?$/;
const DATE_ERROR = 'Le format de date pour le tableau des prix variables est incorrect pour au moins une des dates. Formats acceptés : aaaa-mm ou aaaa-mm-jj hh';
// Nemo PricesTable headers are hard-coded (« Price (€) » is not translated in production).
const PRICE_COLUMNS: ColDef<PriceRow>[] = [
    {
        field: 'date',
        headerName: 'Date',
        editable: true,
        valueParser: ({ newValue }) => String(newValue ?? '').trim(),
        cellRenderer: ({ value }: ICellRendererParams<PriceRow, string>) => (
            <span className={`production-contracts-ellipsis${value && !DATE_FORMAT_REGEX.test(value) ? ' production-contracts-invalid' : ''}`} title={value ?? ''}>{value ?? ''}</span>
        ),
    },
    { field: 'price', headerName: 'Price (€)', editable: true, cellEditor: 'agNumberCellEditor' },
];

const text = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value));

/** Nemo `FuelContractCreate` form inside a `RightSideModal` (create: « Créer un nouveau contrat de combustibles », edit: contract name). */
export default function FuelContractPanel({ contract, existingNames, onClose, onSubmit, error, isPending }: {
    contract?: FuelContract;
    existingNames: string[];
    onClose: () => void;
    onSubmit: (value: FuelContractSubmit) => void;
    error?: string;
    isPending?: boolean;
}) {
    const [name, setName] = useState(contract?.name ?? '');
    const [calorific, setCalorific] = useState(text(contract?.calorificValue ?? DEFAULT_CALORIFIC_VALUE));
    const [fixedPrice, setFixedPrice] = useState(text(contract?.fixedPrice));
    const [description, setDescription] = useState(contract?.description ?? '');
    // Integration pre-fills the create form with the 1900-01 / 3000-12 bounds at 0 €.
    const [prices, setPrices] = useState<PriceRow[]>(
        contract
            ? contract.variablePrices.map((p) => ({ key: nextKey(), date: p.date, price: text(p.price) }))
            : [{ key: nextKey(), date: '1900-01', price: '0' }, { key: nextKey(), date: '3000-12', price: '0' }],
    );
    const [start, setStart] = useState<Record<StartKey, string>>({ startHour: text(contract?.startHour), startDay: text(contract?.startDay), startMonth: text(contract?.startMonth) });
    const [advanced, setAdvanced] = useState(() => !!contract && (CONSTRAINT_KEYS.some((key) => contract.constraints[key] != null) || [contract.startHour, contract.startDay, contract.startMonth].some((v) => v != null)));
    const [constraints, setConstraints] = useState<Record<ConstraintKey, string>>(
        Object.fromEntries(CONSTRAINT_KEYS.map((key) => [key, text(contract?.constraints[key])])) as Record<ConstraintKey, string>,
    );

    const trimmed = name.trim();
    const duplicate = existingNames.some((existing) => existing.toLowerCase() === trimmed.toLowerCase() && existing !== contract?.name);
    const calorificNumber = toNumberOrNull(calorific);
    const fixedNumber = toNumberOrNull(fixedPrice);
    const constraintNumbers = Object.fromEntries(CONSTRAINT_KEYS.map((key) => [key, toNumberOrNull(constraints[key])])) as Record<ConstraintKey, number | null>;
    const invalidConstraint = CONSTRAINT_KEYS.some((key) => Number.isNaN(constraintNumbers[key]) || (constraintNumbers[key] ?? 0) < 0);
    const startNumbers = Object.fromEntries(START_FIELDS.map(({ key }) => [key, toNumberOrNull(start[key])])) as Record<StartKey, number | null>;
    const filledStart = START_FIELDS.filter(({ key }) => start[key] !== '').length;
    const startError = (field: (typeof START_FIELDS)[number]): string | undefined => {
        const value = startNumbers[field.key];
        if (value === null) return filledStart > 0 && filledStart < 3 ? START_ALL_OR_NONE : undefined;
        if (Number.isNaN(value) || !Number.isInteger(value)) return WHOLE_NUMBER;
        if (value < field.min || value > field.max) return field.range;
        return undefined;
    };
    const invalidStart = START_FIELDS.some((field) => !!startError(field));
    const filledPrices = prices.filter((row) => row.date || String(row.price ?? '') !== '');
    const invalidDates = filledPrices.some((row) => !!row.date && !DATE_FORMAT_REGEX.test(row.date));
    const invalidPrices = invalidDates || filledPrices.some((row) => !row.date || Number.isNaN(toNumberOrNull(String(row.price ?? ''))) || (toNumberOrNull(String(row.price ?? '')) ?? 0) < 0);
    const canSubmit = !!trimmed && !duplicate && calorificNumber !== null && calorificNumber > 0 && fixedNumber !== null && !Number.isNaN(fixedNumber) && !invalidConstraint && !invalidStart && !invalidPrices && !isPending;

    const submit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSubmit) return;
        onSubmit({
            name: trimmed,
            calorificValue: calorificNumber!,
            fixedPrice: fixedNumber!,
            description: description.trim(),
            variablePrices: filledPrices.map((row) => ({ date: row.date, price: toNumberOrNull(String(row.price ?? '')) })),
            constraints: constraintNumbers,
            startHour: startNumbers.startHour,
            startDay: startNumbers.startDay,
            startMonth: startNumbers.startMonth,
        });
    };

    return (
        <RightSidePanel
            isOpen
            onClose={onClose}
            title={contract ? contract.name : 'Créer un nouveau contrat de combustibles'}
            testId="fuel-contract-panel"
            footer={
                <>
                    <NJButton label="Annuler" variant="secondary" onClick={onClose} />
                    <NJButton label="Valider" type="submit" form="fuel-contract-form" disabled={!canSubmit} />
                </>
            }
        >
            <form id="fuel-contract-form" className="production-contracts-form production-contracts-panel-form" onSubmit={submit} noValidate>
                {error && <NJInlineMessage variant="error">{error}</NJInlineMessage>}
                <MandatoryNotice />
                <NJFormItem
                    id="fuel-contract-name"
                    label="Nom"
                    isRequired
                    value={name}
                    maxLength={FUEL_CONTRACT_NAME_MAX}
                    onChange={(event) => setName(inputValue(event))}
                    hasError={duplicate}
                    subscriptMessage={duplicate ? 'Un contrat avec ce nom existe déjà.' : `${name.length}/${FUEL_CONTRACT_NAME_MAX} caractères`}
                />
                <NJFormItem
                    id="fuel-contract-calorific"
                    label="Valeur calorifique (J/u)"
                    isRequired
                    type="number"
                    value={calorific}
                    onChange={(event) => setCalorific(inputValue(event))}
                    hasError={calorific !== '' && !(calorificNumber! > 0)}
                    subscriptMessage={calorific !== '' && !(calorificNumber! > 0) ? 'La valeur doit être strictement positive.' : undefined}
                />
                <NJFormItem id="fuel-contract-fixed-price" label="Prix fixe (€/u)" isRequired type="number" value={fixedPrice} onChange={(event) => setFixedPrice(inputValue(event))} />
                <NJFormItem id="fuel-contract-description" label="Description" isMultiline rows={3} value={description} onChange={(event) => setDescription(inputValue(event))} />

                <div className="production-contracts-subsection">
                    <div className="production-contracts-table-title">
                        <NJText scale="sm">Prix variable (mensuel ou horaire)</NJText>
                        <NJTooltip text="Formats acceptés : Mensuel : aaaa-mm · Horaire : aaaa-mm-jj hh" hasArrow placement="bottom">
                            <NJIcon name="info" size="sm" className="production-contracts-info" />
                        </NJTooltip>
                    </div>
                    <EditableRowTable<PriceRow>
                        testId="prices-table"
                        columns={PRICE_COLUMNS}
                        rows={prices}
                        onChange={setPrices}
                        createEmptyRow={emptyPriceRow}
                    />
                    {invalidDates && <NJInlineMessage variant="error">{DATE_ERROR}</NJInlineMessage>}
                </div>

                <NJToggle id="fuel-contract-advanced" label="Afficher les options avancées" checked={advanced} onChange={(_event, checked) => setAdvanced(checked)} />
                {advanced && (
                    <div className="production-contracts-advanced-fields">
                        {START_FIELDS.map((field) => (
                            <NJFormItem
                                key={field.key}
                                id={field.id}
                                label={field.label}
                                type="number"
                                value={start[field.key]}
                                onChange={(event) => setStart((current) => ({ ...current, [field.key]: inputValue(event) }))}
                                hasError={!!startError(field)}
                                subscriptMessage={startError(field)}
                            />
                        ))}
                        {ADVANCED_ORDER.map((key) => {
                            const invalid = Number.isNaN(constraintNumbers[key]) || (constraintNumbers[key] ?? 0) < 0;
                            return (
                                <NJFormItem
                                    key={key}
                                    id={`fuel-contract-${key}`}
                                    label={CONSTRAINT_LABELS[key].replace(/ \(u\)$/, '')}
                                    type="number"
                                    value={constraints[key]}
                                    onChange={(event) => setConstraints((current) => ({ ...current, [key]: inputValue(event) }))}
                                    hasError={invalid}
                                    subscriptMessage={invalid ? POSITIVE : undefined}
                                />
                            );
                        })}
                    </div>
                )}
                {contract && <CreationInfo data={contract} italic />}
            </form>
        </RightSidePanel>
    );
}
